/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The list's Gateway and Status filters. Both are applied here, on the client, to the
 * state the page already polls.
 */

import type { InStoreAsset, InStoreSensor, Threshold } from './types'

export type GatewayFilterValue = 'all' | 'online' | 'offline'
export type StatusFilterValue = 'all' | 'ready' | 'notReady' | 'offline'

export interface ListFilters {
  gateway: GatewayFilterValue
  status: StatusFilterValue
}

export const DEFAULT_FILTERS: ListFilters = { gateway: 'all', status: 'all' }

export const GATEWAY_OPTIONS: { value: GatewayFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
]

export const STATUS_OPTIONS: { value: StatusFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Ready' },
  { value: 'notReady', label: 'Not ready' },
  { value: 'offline', label: 'Offline' },
]

export type Readiness = 'ready' | 'notReady' | 'offline'

/**
 * Offline: no live tag. Ready: a temperature sensor reading inside its location's
 * range. Not ready: everything else that is online — out of range, a probe fault, or
 * a location with no range to be ready against. Doors are only ever offline or not.
 */
export const readiness = (sensor: InStoreSensor, range: Threshold | null): Readiness | null => {
  if (sensor.online !== true) return 'offline'
  if (sensor.type !== 'temperature') return null
  const celsius = sensor.temperatureCelsius
  const inRange =
    range != null &&
    celsius != null &&
    sensor.tagState === 'ok' &&
    celsius >= range.lowCelsius &&
    celsius <= range.highCelsius
  return inRange ? 'ready' : 'notReady'
}

/**
 * The locations whose gateway matches, each with only the sensors whose status
 * matches. A location left with no matching sensor drops out, unless the status
 * filter is off.
 */
export const applyFilters = (locations: InStoreAsset[], filters: ListFilters): InStoreAsset[] =>
  locations.flatMap((asset) => {
    const gatewayOnline = asset.gateway?.online === true
    if (filters.gateway === 'online' && !gatewayOnline) return []
    if (filters.gateway === 'offline' && gatewayOnline) return []
    if (filters.status === 'all') return [asset]
    const sensors = asset.sensors.filter((sensor) => readiness(sensor, asset.threshold) === filters.status)
    return sensors.length > 0 ? [{ ...asset, sensors }] : []
  })

export const isFiltered = (filters: ListFilters): boolean =>
  filters.gateway !== DEFAULT_FILTERS.gateway || filters.status !== DEFAULT_FILTERS.status

/**
 * The locations matching a search: a location whose name matches keeps all its sensors; otherwise
 * it stays with just the sensors whose name or serial number matches, or drops out. Case-insensitive.
 */
export const applySearch = (locations: InStoreAsset[], searchTerm: string): InStoreAsset[] => {
  const needle = searchTerm.trim().toLowerCase()
  if (!needle) return locations
  const matches = (text: string | number | null | undefined) =>
    text != null && String(text).toLowerCase().includes(needle)
  return locations.flatMap((asset) => {
    if (matches(asset.name)) return [asset]
    const sensors = asset.sensors.filter((sensor) => matches(sensor.name) || matches(sensor.serialNumber))
    return sensors.length > 0 ? [{ ...asset, sensors }] : []
  })
}
